import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Typography, Spin, Result, Descriptions, Tag } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { fetchBillVerification } from '../api';
import aurikshaLogo from '../../../assets/auriksha_logo.png';

const { Title, Text } = Typography;

const formatAmount = (amount, currency = 'INR') => {
  if (typeof amount !== 'number' || isNaN(amount)) return '-';
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `₹ ${amount.toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  return isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const BillVerification = () => {
  const { id } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchBillVerification(id);
        if (mounted) setState({ loading: false, data, error: false });
      } catch {
        if (mounted) setState({ loading: false, data: null, error: true });
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  const { loading, data, error } = state;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#f0f2f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <Card style={{ width: '100%', maxWidth: 460, borderRadius: 10 }} bodyStyle={{ padding: 24 }}>
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <img src={aurikshaLogo} alt="Auriksha" style={{ height: 48, marginBottom: 8 }} />
          <Title level={4} style={{ margin: 0 }}>Bill Verification</Title>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12 }}><Text type="secondary">Verifying bill…</Text></div>
          </div>
        ) : error ? (
          <Result
            status="warning"
            title="Unable to verify"
            subTitle="We couldn't verify this bill right now. Please try again later."
          />
        ) : !data?.verified ? (
          <Result
            status="error"
            title="Not a valid bill"
            subTitle="This QR code does not match any bill in our records."
          />
        ) : (
          <>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <CheckCircleFilled style={{ fontSize: 44, color: '#52c41a' }} />
              <div style={{ marginTop: 8 }}>
                <Tag color="success" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 16 }}>
                  Verified genuine
                </Tag>
              </div>
            </div>

            <Descriptions column={1} bordered size="small" labelStyle={{ width: 130, fontWeight: 500 }}>
              <Descriptions.Item label="Bill ID">{data.billId || '-'}</Descriptions.Item>
              <Descriptions.Item label="Date">{formatDate(data.date)}</Descriptions.Item>
              <Descriptions.Item label="Final Amount">
                {formatAmount(data.finalAmount, data.currency)}
              </Descriptions.Item>
              {data.branch && (
                <Descriptions.Item label="Branch">{data.branch}</Descriptions.Item>
              )}
              <Descriptions.Item label="Issued by">{data.company || '-'}</Descriptions.Item>
            </Descriptions>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                This confirms the bill was issued by {data.company || 'the company'}.
                For full details, please refer to your printed invoice.
              </Text>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default BillVerification;
